import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { Action } from "shared/types/Action"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import { ProjectActionType } from "../types/ProjectActionType"
import { FilteredProjectScopedActionHandlers } from "./useProjectScopedActionHandler"

export const PROJECT_ACTION_ORDER: ProjectActionType[] = [
  ProjectActionType.OPEN,
  ProjectActionType.EDIT,
  ProjectActionType.NEW_PRODUCT,
  ProjectActionType.NEW_ROOM,
  ProjectActionType.NEW_FLOOR,
  ProjectActionType.EXPORT,
  ProjectActionType.GAEB_EXPORT,
  ProjectActionType.MUSTERHEFT_EXPORT,
  ProjectActionType.ATTACHMENTS,
  ProjectActionType.SHARE,
  ProjectActionType.DUPLICATE,
  ProjectActionType.ACTIVATE,
  ProjectActionType.MANAGE_TEAM,
  ProjectActionType.LEAVE_TEAM,
  ProjectActionType.INVITE_MEMBER,
  ProjectActionType.ADD_CLARIFICATION_REQUEST,
  ProjectActionType.HISTORY,
  ProjectActionType.DELETE
]

export const REDUCED_PROJECT_ACTION_TYPES = [
  ProjectActionType.SHARE,
  ProjectActionType.DUPLICATE,
  ProjectActionType.ACTIVATE,
  ProjectActionType.EXPORT,
  ProjectActionType.GAEB_EXPORT,
  ProjectActionType.DELETE
]

export interface UseProjectActionsArgs
  extends Partial<FilteredProjectScopedActionHandlers> {
  idPrefix: string
  isShared?: boolean
  isNewProductDisabled?: boolean
  isNewRoomDisabled?: boolean
  isNewFloorDisabled?: boolean
  isEditDisabled?: boolean
  isShareDisabled?: boolean
  isExportDisabled?: boolean
  isGaebExportDisabled?: boolean
  isMusterheftExportDisabled?: boolean
  isOpenAttachmentsDisabled?: boolean
  isProjectHistoryDisabled?: boolean
  isDuplicateDisabled?: boolean
  isDeleteDisabled?: boolean
  additionalActions?: Action[]

  allowedActions?: ProjectActionType[]
}

export function useProjectActions({
  idPrefix,
  isShared,
  onOpen,
  onNewProduct,
  isNewProductDisabled,
  onNewRoom,
  isNewRoomDisabled,
  onNewFloor,
  isNewFloorDisabled,
  onEdit,
  isEditDisabled,
  onAddClarificationRequest,
  onShare,
  isShareDisabled,
  onExport,
  isExportDisabled,
  onGaebExport,
  isGaebExportDisabled,
  onMusterheftExport,
  isMusterheftExportDisabled,
  onOpenAttachments,
  isProjectHistoryDisabled,
  onOpenProjectHistory,
  isOpenAttachmentsDisabled,
  onDuplicate,
  isDuplicateDisabled,
  onActivate,
  onInviteMember,
  onLeaveTeam,
  onManageTeam,
  onDelete,
  isDeleteDisabled,
  additionalActions,

  allowedActions
}: UseProjectActionsArgs) {
  const { t } = useTranslation()

  const openProjectAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__open`,
      "data-testid": "project__actions__open",
      label: t("Project.Actions.Open"),
      icon: IconName.EDIT,
      onClick: onOpen
    }),
    [onOpen]
  )

  const newProductAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__add-variant`,
      "data-testid": "project__actions__add-variant",
      label: t("ProductPosition.Actions.Create"),
      icon: IconName.PRODUCTS,
      onClick: onNewProduct
        ? () => onNewProduct?.({ hideProject: true })
        : undefined,
      disabled: isNewProductDisabled
    }),
    [onNewProduct, isNewProductDisabled]
  )

  const newRoomAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__add-room`,
      "data-testid": "project__actions__add-room",
      label: t("Room.Actions.Create"),
      icon: IconName.ROOM,
      onClick: onNewRoom ? () => onNewRoom?.({ hideProject: true }) : undefined,
      disabled: isNewRoomDisabled
    }),
    [onNewRoom, isNewRoomDisabled]
  )

  const newFloorAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__add-floor`,
      "data-testid": "project__actions__add-floor",
      label: t("Floor.Actions.Create"),
      icon: IconName.FLOOR,
      onClick: onNewFloor
        ? () => onNewFloor?.({ hideProject: true })
        : undefined,
      disabled: isNewFloorDisabled
    }),
    [onNewFloor, isNewFloorDisabled, idPrefix, t]
  )

  const editAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__edit`,
      key: "project__actions__edit",
      "data-testid": "project__actions__edit",
      icon: IconName.EDIT,
      label: t("Project.Actions.Update"),
      onClick: onEdit,
      disabled: isEditDisabled
    }),
    [onEdit, isEditDisabled]
  )

  const addClarificationRequestAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__add-clarification-request`,
      key: "project__actions__add-clarification-request",
      "data-testid": "project__actions__add-clarification-request",
      icon: IconName.CLARIFICATION_REQUEST,
      label: t("Project.Actions.AddClarificationRequest"),
      onClick: onAddClarificationRequest
    }),
    [onAddClarificationRequest]
  )

  const shareAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__share`,
      key: "project__actions__share",
      "data-testid": "project__actions__share",
      icon: IconName.SHARE,
      label: t("Project.Actions.Share"),
      onClick: onShare,
      disabled: isShareDisabled
    }),
    [onShare, isShareDisabled]
  )

  const exportAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__export`,
      key: "project__actions__export",
      "data-testid": "project__actions__export",
      icon: IconName.EXPORT,
      label: t("Project.Actions.ExportPDF"),
      onClick: onExport,
      disabled: isExportDisabled
    }),
    [onExport, isExportDisabled]
  )

  const exportGaebAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__gaeb`,
      key: "project__actions__export-gaeb",
      "data-testid": "project__actions__export-gaeb",
      icon: IconName.EXPORT,
      label: t("Project.Actions.ExportGAEB"),
      onClick: onGaebExport,
      disabled: isGaebExportDisabled
    }),
    [onGaebExport, isGaebExportDisabled]
  )

  const musterheftExportAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__musterheft`,
      key: "project__actions__export-musterheft",
      "data-testid": "project__actions__export-musterheft",
      icon: IconName.EXPORT,
      label: t("Project.Actions.ExportMusterheft"),
      onClick: onMusterheftExport,
      disabled: isMusterheftExportDisabled
    }),
    [onMusterheftExport, isMusterheftExportDisabled]
  )

  const openProjectHistoryAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__history`,
      key: "project__actions__history",
      "data-testid": "project__actions__history",
      icon: IconName.HISTORY,
      label: t("Project.Actions.History"),
      onClick: onOpenProjectHistory,
      disabled: isProjectHistoryDisabled
    }),
    [onOpenProjectHistory]
  )

  const attachmentsAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__attachments`,
      key: "project__actions__attachments",
      "data-testid": "project__actions__attachments",
      icon: IconName.PAPERCLIP,
      label: t("Project.Actions.Attachments"),
      onClick: onOpenAttachments,
      disabled: isOpenAttachmentsDisabled
    }),
    [onOpenAttachments, isOpenAttachmentsDisabled]
  )

  const duplicateAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__duplicate`,
      key: "project__actions__duplicate",
      "data-testid": "project__actions__duplicate",
      icon: IconName.DUPLICATE,
      label: isShared
        ? t("Project.Actions.AddToMyProjects")
        : t("Project.Actions.Duplicate"),
      onClick: onDuplicate,
      disabled: isDuplicateDisabled
    }),
    [onDuplicate, isDuplicateDisabled]
  )

  const activateAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__activate`,
      key: "project__actions__activate",
      "data-testid": "project__actions__activate",
      icon: IconName.TARGET,
      label: t("Project.Actions.Activate"),
      onClick: onActivate
    }),
    [onActivate]
  )

  const inviteTeamMemberAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__invite-member`,
      key: "project__actions__invite-member",
      "data-testid": "project__actions__invite-member",
      icon: IconName.USER_PLUS,
      label: t("Project.Actions.InviteTeamMembers"),
      onClick: onInviteMember
    }),
    [onInviteMember]
  )

  const manageTeamAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__manage-team`,
      key: "project__actions__manage-team",
      "data-testid": "project__actions__manage-team",
      icon: IconName.TEAM,
      label: t("ProjectTeam.Actions.Manage"),
      onClick: onManageTeam
    }),
    [onManageTeam]
  )

  const leaveTeamAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__leave-team`,
      key: "project__actions__leave-team",
      "data-testid": "project__actions__leave-team",
      icon: IconName.LOGOUT_LEFT,
      label: t("Project.Actions.LeaveTeam"),
      onClick: onLeaveTeam
    }),
    [onLeaveTeam]
  )

  const deleteAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__delete`,
      key: "project__actions__delete",
      "data-testid": "project__actions__delete",
      icon: IconName.BIN,
      label: t("Project.Actions.Delete"),
      isDanger: true,
      onClick: onDelete,
      disabled: isDeleteDisabled
    }),
    [onDelete, isDeleteDisabled]
  )

  const allActions: Record<ProjectActionType, Action> = useMemo(
    () => ({
      [ProjectActionType.OPEN]: openProjectAction,
      [ProjectActionType.EDIT]: editAction,
      [ProjectActionType.NEW_PRODUCT]: newProductAction,
      [ProjectActionType.NEW_ROOM]: newRoomAction,
      [ProjectActionType.NEW_FLOOR]: newFloorAction,
      [ProjectActionType.EXPORT]: exportAction,
      [ProjectActionType.GAEB_EXPORT]: exportGaebAction,
      [ProjectActionType.MUSTERHEFT_EXPORT]: musterheftExportAction,
      [ProjectActionType.ATTACHMENTS]: attachmentsAction,
      [ProjectActionType.SHARE]: shareAction,
      [ProjectActionType.DUPLICATE]: duplicateAction,
      [ProjectActionType.ACTIVATE]: activateAction,
      [ProjectActionType.MANAGE_TEAM]: manageTeamAction,
      [ProjectActionType.LEAVE_TEAM]: leaveTeamAction,
      [ProjectActionType.INVITE_MEMBER]: inviteTeamMemberAction,
      [ProjectActionType.HISTORY]: openProjectHistoryAction,
      [ProjectActionType.ADD_CLARIFICATION_REQUEST]:
        addClarificationRequestAction,
      [ProjectActionType.DELETE]: deleteAction
    }),
    [
      openProjectAction,
      editAction,
      addClarificationRequestAction,
      newProductAction,
      newRoomAction,
      newFloorAction,
      exportAction,
      exportGaebAction,
      musterheftExportAction,
      attachmentsAction,
      shareAction,
      duplicateAction,
      activateAction,
      manageTeamAction,
      inviteTeamMemberAction,
      leaveTeamAction,
      deleteAction
    ]
  )

  const actions = useMemo(() => {
    const allowed = new Set(allowedActions)
    const ordered = PROJECT_ACTION_ORDER.filter(
      type => !allowedActions || allowed.has(type)
    )
    const all: (Action | undefined)[] = ordered.map(type => allActions[type])
    if (additionalActions) {
      const deleteIdx = ordered.indexOf(ProjectActionType.DELETE)
      all.splice(
        deleteIdx === -1 ? all.length : deleteIdx,
        0,
        ...additionalActions
      )
    }
    return all.filter((action): action is Action => !!action?.onClick)
  }, [allActions, allowedActions, additionalActions])

  return {
    actions,
    openProjectAction,
    newProductAction,
    newRoomAction,
    newFloorAction,
    editAction,
    addClarificationRequestAction,
    shareAction,
    exportAction,
    exportGaebAction,
    musterheftExportAction,
    attachmentsAction,
    duplicateAction,
    activateAction,
    inviteTeamMemberAction,
    manageTeamAction,
    leaveTeamAction,
    deleteAction
  }
}
